import { supabase } from "@/integrations/supabase/client";
import { Contact } from "@/types/contacts";
import { BaseEntity } from "@/types/entities";

// Tipos de resultado
export interface DuplicateCheckResult<T> {
  new: T[];
  existing: T[];
  duplicates: T[];
  similarPairs?: SimilarPair<T>[];
}

export interface SimilarPair<T> {
  csvEntity: T;
  dbEntity: T;
  similarityReason: string;
  confidence: 'high' | 'medium' | 'low';
}

// ============= NORMALIZAÇÃO E SIMILARIDADE =============

/**
 * Normaliza texto para comparação de duplicatas
 * Remove acentos, espaços extras, normaliza hífens
 */
export function normalizeForComparison(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD') // Decompõe caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remove marcas de acento
    .replace(/\s+/g, ' ') // Normaliza espaços múltiplos
    .replace(/\s*-\s*/g, '-') // Normaliza hífens (remove espaços ao redor)
    .trim();
}

/**
 * Verifica se dois nomes são similares
 * Detecta subsidiárias, variações e unidades
 */
export function areSimilarNames(name1: string, name2: string): { 
  similar: boolean; 
  reason: string;
  confidence: 'high' | 'medium' | 'low';
} {
  const n1 = normalizeForComparison(name1);
  const n2 = normalizeForComparison(name2);
  
  // Nomes idênticos após normalização
  if (n1 === n2) {
    return { similar: true, reason: 'Nome idêntico', confidence: 'high' };
  }
  
  // Um nome contém o outro (subsidiárias ou variações)
  if (n1.includes(n2) || n2.includes(n1)) {
    const shorter = n1.length < n2.length ? n1 : n2;
    const longer = n1.length < n2.length ? n2 : n1;
    const diff = longer.replace(shorter, '').trim();
    
    // Verificar se a diferença indica unidades específicas (manter separado)
    const unitIndicators = /\b(shopping|unidade|filial|loja|matriz|centro|bairro|norte|sul|leste|oeste)\b/i;
    if (unitIndicators.test(diff)) {
      return { similar: false, reason: 'Unidades diferentes', confidence: 'high' };
    }
    
    // Se a diferença é pequena, provavelmente é uma variação
    if (diff.length < 10) {
      return { similar: true, reason: 'Variação do nome', confidence: 'high' };
    }
    
    return { similar: true, reason: 'Nome subsidiário', confidence: 'medium' };
  }
  
  // Verificar similaridade por tokens (palavras)
  const tokens1 = n1.split(/\s+/);
  const tokens2 = n2.split(/\s+/);
  const commonTokens = tokens1.filter(t => tokens2.includes(t));
  
  // Se mais de 60% das palavras são iguais
  const similarity = commonTokens.length / Math.max(tokens1.length, tokens2.length);
  if (similarity >= 0.6 && commonTokens.length >= 2) {
    return { similar: true, reason: 'Alta similaridade de palavras', confidence: 'low' };
  }
  
  return { similar: false, reason: 'Nomes diferentes', confidence: 'high' };
}

// Verificação de duplicação para Contatos
export const checkContactDuplicates = async (
  contacts: Contact[]
): Promise<DuplicateCheckResult<Contact>> => {
  if (contacts.length === 0) {
    return { new: [], existing: [], duplicates: [] };
  }

  // Cria arrays de emails e companies para busca otimizada
  const emails = contacts.map(c => c.email).filter(Boolean);
  const companies = contacts.map(c => c.company).filter(Boolean);

  // Busca contatos existentes no banco com email+company
  const { data: existingContacts, error } = await supabase
    .from("contacts")
    .select("*")
    .in("email", emails)
    .in("company", companies);

  if (error) throw error;

  // Mapeia contatos existentes para verificação rápida
  const existingMap = new Map<string, any>();
  (existingContacts || []).forEach(contact => {
    const key = `${contact.email}|${contact.company}`.toLowerCase();
    existingMap.set(key, contact);
  });

  const newContacts: Contact[] = [];
  const existing: Contact[] = [];
  const duplicates: Contact[] = [];

  contacts.forEach(contact => {
    const key = `${contact.email}|${contact.company}`.toLowerCase();
    const existingContact = existingMap.get(key);

    if (existingContact) {
      // Contato já existe, adiciona o ID do banco
      const contactWithId = { ...contact, id: existingContact.id };
      existing.push(contactWithId);
      duplicates.push(contactWithId);
    } else {
      newContacts.push(contact);
    }
  });

  return { new: newContacts, existing, duplicates };
};

// Verificação de duplicação para Entidades com detecção inteligente
export const checkEntityDuplicates = async <T extends BaseEntity>(
  entities: T[],
  type: 'restaurant' | 'operator' | 'consolidator'
): Promise<DuplicateCheckResult<T>> => {
  if (entities.length === 0) {
    return { new: [], existing: [], duplicates: [], similarPairs: [] };
  }

  // Busca TODAS as entidades do tipo no banco para comparação inteligente
  const { data: existingEntities, error } = await supabase
    .from("entities")
    .select("*")
    .eq("type", type);

  if (error) throw error;

  const newEntities: T[] = [];
  const existing: T[] = [];
  const duplicates: T[] = [];
  const similarPairs: SimilarPair<T>[] = [];

  // Mapeia entidades existentes por chave normalizada para duplicatas exatas
  const exactMatchMap = new Map<string, any>();
  (existingEntities || []).forEach(entity => {
    const key = `${normalizeForComparison(entity.name)}|${normalizeForComparison(entity.state || '')}`;
    exactMatchMap.set(key, entity);
  });

  entities.forEach(entity => {
    // 1. Verificar duplicata exata (nome + estado normalizados)
    const exactKey = `${normalizeForComparison(entity.name)}|${normalizeForComparison(entity.state || '')}`;
    const exactMatch = exactMatchMap.get(exactKey);

    if (exactMatch) {
      // Duplicata exata encontrada
      const entityWithId = { ...entity, id: exactMatch.id } as T;
      existing.push(entityWithId);
      duplicates.push(entityWithId);
      return;
    }

    // 2. Verificar similaridade com todas as entidades do mesmo estado
    let foundSimilar = false;
    const entityState = normalizeForComparison(entity.state || '');
    
    for (const dbEntity of existingEntities || []) {
      const dbState = normalizeForComparison(dbEntity.state || '');
      
      // Só compara se for o mesmo estado
      if (entityState !== dbState) continue;

      const similarity = areSimilarNames(entity.name, dbEntity.name);
      
      if (similarity.similar) {
        // Encontrou entidade similar
        const dbEntityConverted = {
          id: dbEntity.id,
          name: dbEntity.name,
          region: dbEntity.region || "",
          state: dbEntity.state || "",
          registrationForm: dbEntity.registration_form || false,
          form: dbEntity.form || false,
          logo: dbEntity.logo || false,
          observations: dbEntity.observations || "",
          instagram: dbEntity.instagram || ""
        } as T;

        similarPairs.push({
          csvEntity: entity,
          dbEntity: dbEntityConverted,
          similarityReason: similarity.reason,
          confidence: similarity.confidence
        });
        
        foundSimilar = true;
        break;
      }
    }

    if (!foundSimilar) {
      // Entidade realmente nova
      newEntities.push(entity);
    }
  });

  return { new: newEntities, existing, duplicates, similarPairs };
};

// Verifica se um único contato já existe
export const checkSingleContactDuplicate = async (
  email: string,
  company: string,
  excludeId?: number
): Promise<boolean> => {
  let query = supabase
    .from("contacts")
    .select("id")
    .eq("email", email)
    .eq("company", company);

  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query.single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  
  return !!data;
};

// Verifica se uma única entidade já existe
export const checkSingleEntityDuplicate = async (
  name: string,
  type: string,
  state: string,
  excludeId?: number
): Promise<boolean> => {
  let query = supabase
    .from("entities")
    .select("id")
    .eq("name", name)
    .eq("type", type)
    .eq("state", state);

  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query.single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  
  return !!data;
};
